/**
 * Platform Feature Flags Registry.
 * Defines canonical feature flag keys, descriptions, categories, and offline/fallback defaults
 * for Firebase Remote Config integration across the Memories platform.
 */

export type FeatureFlagCategory =
  | "editor"
  | "media"
  | "messaging"
  | "safety"
  | "diagnostics"
  | "navigation";

export interface IFeatureFlagDefinition {
  key: string;
  name: string;
  description: string;
  category: FeatureFlagCategory;
  defaultValue: boolean;
}

/**
 * Standard registry of platform feature flags.
 */
export const PLATFORM_FEATURE_FLAGS: Record<string, IFeatureFlagDefinition> = {
  enable_collaborative_editor: {
    key: "enable_collaborative_editor",
    name: "Collaborative Memory Editor",
    description: "Enable real-time collaborative WYSIWYG memory editing powered by Tiptap & Yjs.",
    category: "editor",
    defaultValue: true,
  },
  enable_media_safety_scanning: {
    key: "enable_media_safety_scanning",
    name: "Media Safety Content Moderation",
    description: "Perform automated image analysis and safety scanning before media upload.",
    category: "safety",
    defaultValue: true,
  },
  enable_content_reporting: {
    key: "enable_content_reporting",
    name: "Inappropriate Content Reporting",
    description: "Allow users to submit inappropriate content moderation reports to system administrators.",
    category: "safety",
    defaultValue: true,
  },
  enable_crashlytics_telemetry: {
    key: "enable_crashlytics_telemetry",
    name: "Crashlytics Diagnostic Telemetry",
    description: "Transmit client error stack traces and crash tombstones to Firebase Crashlytics.",
    category: "diagnostics",
    defaultValue: true,
  },
  enable_d3_visualiser: {
    key: "enable_d3_visualiser",
    name: "D3 Memory Visualiser",
    description: "Enable interactive D3 graph rendering showing connections between memories and media.",
    category: "navigation",
    defaultValue: true,
  },
  enable_extract_media_info: {
    key: "enable_extract_media_info",
    name: "Extract Media Info",
    description: "Extract metadata, EXIF dates, geolocations, and AI tags with comparison review modal.",
    category: "media",
    defaultValue: true,
  },
  enable_beta_features: {
    key: "enable_beta_features",
    name: "Beta Group Early Access Features",
    description: "Unlock experimental features and early access UI for users assigned to a Beta Group.",
    category: "diagnostics",
    defaultValue: false,
  },
};

/**
 * Default boolean values map used for Remote Config baseline initialization.
 */
export const DEFAULT_REMOTE_CONFIG_DEFAULTS: Record<string, boolean> = Object.values(
  PLATFORM_FEATURE_FLAGS
).reduce((acc, flag) => {
  acc[flag.key] = flag.defaultValue;
  return acc;
}, {} as Record<string, boolean>);
